import { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, User, Bot, Clock, CheckCircle, AlertTriangle, RefreshCw, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function AdminChats() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [chatCounts, setChatCounts] = useState({ active: 0, escalated: 0, total: 0 });
  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);

  // Load chat sessions
  const loadSessions = async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await api.get(`/admin/chat/sessions${params}`);
      setSessions(response.data);
      
      // Also load counts
      const countsResponse = await api.get('/admin/chat/active-count');
      setChatCounts(countsResponse.data);
    } catch (err) {
      console.error('Failed to load chat sessions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load and polling for real-time updates
  useEffect(() => {
    loadSessions();
    
    // Poll every 5 seconds for new messages
    pollingRef.current = setInterval(loadSessions, 5000);
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [statusFilter]);

  // Scroll to bottom when viewing a session
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedSession]);

  // Refresh selected session when it changes in the sessions list
  useEffect(() => {
    if (selectedSession) {
      const updated = sessions.find(s => s.id === selectedSession.id);
      if (updated && JSON.stringify(updated) !== JSON.stringify(selectedSession)) {
        setSelectedSession(updated);
      }
    }
  }, [sessions, selectedSession?.id]);

  const selectSession = async (session) => {
    try {
      const response = await api.get(`/admin/chat/sessions/${session.id}`);
      setSelectedSession(response.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Chat-Session');
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedSession || isSending) return;
    
    setIsSending(true);
    try {
      await api.post(`/admin/chat/sessions/${selectedSession.id}/reply`, {
        message: replyText
      });
      
      setReplyText('');
      
      // Refresh the session
      const response = await api.get(`/admin/chat/sessions/${selectedSession.id}`);
      setSelectedSession(response.data);
      
      toast.success('Antwort gesendet');
    } catch (err) {
      toast.error('Fehler beim Senden der Antwort');
    } finally {
      setIsSending(false);
    }
  };

  const updateStatus = async (sessionId, newStatus) => {
    try {
      await api.patch(`/admin/chat/sessions/${sessionId}/status`, { status: newStatus });
      
      // Refresh sessions
      loadSessions();
      
      // Update selected session if it's the one being updated
      if (selectedSession?.id === sessionId) {
        setSelectedSession(prev => ({ ...prev, status: newStatus }));
      }
      
      toast.success(`Status auf "${getStatusLabel(newStatus)}" geändert`);
    } catch (err) {
      toast.error('Fehler beim Aktualisieren des Status');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Aktiv</Badge>;
      case 'escalated':
        return <Badge className="bg-orange-100 text-orange-700">Eskaliert</Badge>;
      case 'resolved':
        return <Badge className="bg-gray-100 text-gray-600">Gelöst</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Aktiv';
      case 'escalated': return 'Eskaliert';
      case 'resolved': return 'Gelöst';
      default: return status;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendReply();
    }
  };

  return (
    <div className="p-6" data-testid="admin-chats">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kundenservice Chats</h1>
          <p className="text-gray-600 mt-1">Verwalten Sie Kundenanfragen in Echtzeit</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Status counts */}
          <div className="flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1 text-green-600">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              {chatCounts.active} Aktiv
            </span>
            <span className="flex items-center gap-1 text-orange-600">
              <AlertTriangle className="w-4 h-4" />
              {chatCounts.escalated} Eskaliert
            </span>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={loadSessions}
            data-testid="refresh-chats-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
        {/* Sessions List */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col">
          {/* Filter */}
          <div className="p-4 border-b border-gray-100">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1B30]/50"
              data-testid="status-filter"
            >
              <option value="">Alle Chats</option>
              <option value="active">Aktiv</option>
              <option value="escalated">Eskaliert</option>
              <option value="resolved">Gelöst</option>
            </select>
          </div>

          {/* Sessions */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1B30] mx-auto mb-2" />
                Laden...
              </div>
            ) : sessions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Keine Chat-Sessions gefunden</p>
              </div>
            ) : (
              sessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => selectSession(session)}
                  className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedSession?.id === session.id ? 'bg-[#9B1B30]/5 border-l-4 border-l-[#9B1B30]' : ''
                  }`}
                  data-testid={`chat-session-${session.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {session.customer_name || 'Gast'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {session.customer_email || 'Keine E-Mail'}
                      </p>
                    </div>
                    {getStatusBadge(session.status)}
                  </div>
                  <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                    {session.messages?.[session.messages.length - 1]?.content || 'Keine Nachrichten'}
                  </p>
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTime(session.updated_at)}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Detail */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col">
          {selectedSession ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-900">{selectedSession.customer_name || 'Gast'}</h2>
                  <p className="text-sm text-gray-500">{selectedSession.customer_email || 'Keine E-Mail'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedSession.status)}
                  <select
                    value={selectedSession.status}
                    onChange={(e) => updateStatus(selectedSession.id, e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#9B1B30]/50"
                    data-testid="change-status-select"
                  >
                    <option value="active">Aktiv</option>
                    <option value="escalated">Eskaliert</option>
                    <option value="resolved">Gelöst</option>
                  </select>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {selectedSession.messages?.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'customer' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        msg.role === 'customer'
                          ? 'bg-[#9B1B30] text-white rounded-br-md'
                          : msg.role === 'admin'
                          ? 'bg-blue-600 text-white rounded-bl-md'
                          : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {msg.role === 'customer' ? (
                          <User className="w-3 h-3" />
                        ) : msg.role === 'admin' ? (
                          <User className="w-3 h-3" />
                        ) : (
                          <Bot className="w-3 h-3 text-[#9B1B30]" />
                        )}
                        <span className={`text-xs font-medium ${
                          msg.role === 'customer' || msg.role === 'admin' ? 'text-white/80' : 'text-gray-500'
                        }`}>
                          {msg.role === 'customer' ? 'Kunde' : msg.role === 'admin' ? (msg.admin_name || 'Admin') : 'KI-Assistent'}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-1 ${
                        msg.role === 'customer' || msg.role === 'admin' ? 'text-white/60' : 'text-gray-400'
                      }`}>
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Input */}
              <div className="p-4 border-t border-gray-100 bg-white">
                <div className="flex items-center gap-2">
                  <Input
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Antwort schreiben..."
                    disabled={isSending}
                    className="flex-1"
                    data-testid="admin-reply-input"
                  />
                  <Button
                    onClick={sendReply}
                    disabled={!replyText.trim() || isSending}
                    className="bg-[#9B1B30] hover:bg-[#7B1526]"
                    data-testid="admin-send-reply-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Senden
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Ihre Antwort wird dem Kunden direkt im Chat angezeigt
                </p>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Wählen Sie einen Chat aus</p>
                <p className="text-sm mt-1">um die Konversation anzuzeigen</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
